/* globals zoomSdk */
import { Log, LogLevel } from "one-log";

import { init } from "ccw-app";

import { loader } from "one-web-loader";
import { enableTrace } from "one-trace";

import { AlertNotification } from "one-alert-notification";

import { BrowserLogger } from "one-web-logger";
import { HAServer, HAServerOne } from "one-server-local-ha";

import { ErrorObject } from "one-state-error/src/errorobject";

import { apis } from "./apis";

// set up the logger
const log = new Log("ccw-app", [new BrowserLogger()], LogLevel.DEBUG);

const LOG_TAG = "ccw-app-zoom";
/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

interface EngagementVariable {
  id: string;
  name: string;
  value: string;
}

interface EngagementVariableResponse {
  variables: EngagementVariable[];
}

const PHONE_CALL_STATUS_MAP ={
  "ACTIVE": "active",
  "OUTGOING": "outgoing",
  "INCOMING": "incoming",
  "IDLE": "idle",
  "HOLD": "hold",
  "NOT_REGISTERED": "not_registered"
}

/* ------------------------------------------------------------------ */
/* Engagement variable cache (CRITICAL)                                */
/* ------------------------------------------------------------------ */

let engagementVariableCache: Record<string, string> = {};
let currentEngagementId: string | null = null;

const getEngagementVariable = (name: string): string =>
  engagementVariableCache[name] ?? "";

loader(
  {
    log,
    url: "settings.json",
  },
  (options: any) =>
    new Promise(async (resolve) => {
      const { settings, resolver } = options;

      /**
       * check for the log level config from settings and set default
       */
      const { logconfig, alertnotify } = settings;
      if (logconfig?.level === "INFO") {
        log.setlevel(LogLevel.INFO);
      } else if (logconfig?.level === "WARN") {
        log.setlevel(LogLevel.WARN);
      } else if (logconfig?.level === "ERROR") {
        log.setlevel(LogLevel.ERROR);
      } else {
        log.setlevel(LogLevel.DEBUG);
      }
      log.debug("ccw-app-web", "app settings", {
        settings,
      });

      // logged in agent id
      const agentId = "761012";
      // adding ani
      const ani = "";
      let alertnotification = new AlertNotification(
        false,
        alertnotify?.url,
        {}
      );
      if (alertnotify?.enable) {
        alertnotification = new AlertNotification(true, alertnotify?.url, {
          customer_name: alertnotify?.customer,
          server_id: alertnotify?.servername,
          app_type: "web",
        });
      }
      // enable user specific trace
      enableTrace(agentId, {
        log,
        settings,
      });

      // initialize the app
      const { store } = await init("app", {
        log,
        agentId,
        ani,
        settings,
        resolver,
        alertnotification,
        server: new HAServer(settings["servers"], (url: string, error: any) => {
          store.dispatch({
            type: "one-state-errorhandle/set",
            payload: {
              url: url,
              error: error,
              monitor: false,
            },
          });
        }),
        serverone: new HAServerOne(
          settings["servers"],
          (url: string, error: any) => {
            store.dispatch({
              type: "one-state-errorhandle/set",
              payload: {
                url: url,
                error: error,
                monitor: false,
              },
            });
          }
        ),
      });

      resolve({
        log,
        // next is optional
        next: (params: any) => {
          console.debug("ccw-app-web", "finished initialization...", {
            params,
          });
          return Promise.resolve({
            store,
          });
        },
      });

      const configureSdk = async () => {
        try {
          const configResponse = await zoomSdk.config({
            capabilities: [
              // apis demoed in the buttons
              ...apis.map((api) => api.name), // IMPORTANT

              // demo events
              "onSendAppInvitation",
              "onShareApp",
              "onActiveSpeakerChange",
              "onMeeting",

              // connect api and event
              "connect",
              "onConnect",
              "postMessage",
              "onMessage",
              "onEngagementEnd", // depricated
              "onEngagementContextChange",
              "getEngagementContext",
              "getEngagementStatus",
              "getEngagementVariableValue",
              "onEngagementVariableValueChange",
              "onEngagementStatusChange", // newly added
              // in-client api and event
              "authorize",
              "getAppContext",
              "onAuthorized",
              "promptAuthorize",
              "getUserContext",
              "onMyUserContextChange",
              "sendAppInvitationToAllParticipants",
              "sendAppInvitation",

              // Zoom Phone
              "getPhoneContext",
              "onPhoneContext",
              "onPhoneCalleeAnswered",
              "onPhoneCallerEnded",
              "onPhoneCalleeEnded",
              "onPhoneCalleeRejected",
              "getRunningContext",
            ],
            version: "0.16",
          });
          console.log(LOG_TAG, "App configured: ", configResponse);
          zoomSdk.onSendAppInvitation((data: any) => {
            console.log(LOG_TAG, data);
          });
          zoomSdk.onShareApp((data: any) => {
            console.log(LOG_TAG, data);
          });

          zoomSdk.addEventListener("onEngagementEnd", (message: any) => {
            console.log(LOG_TAG, "onEngagementEnd event:");
            console.log(LOG_TAG, message);
          });

          zoomSdk.addEventListener("onMessage", (event: any) => {
            console.log(LOG_TAG, "onMessage event: ");
          });
          console.log(LOG_TAG, "onMessage event installed!");

          zoomSdk.addEventListener(
            "onEngagementContextChange",
            (message: any) => {
              console.log(LOG_TAG, "onEngagementContextChange event: ");
              console.log(LOG_TAG, message);
         
              getEngagementStatus();
            }
          );
        /* ------------------------------------------------------------------ */
        /* Variable change listener (MOST IMPORTANT)                           */
        /* ------------------------------------------------------------------ */
           zoomSdk.addEventListener(
            "onEngagementVariableValueChange",
            async (event: any) => {
              console.log(LOG_TAG, "Variable change event:", event);

              event?.variables?.forEach((v: EngagementVariable) => {
                engagementVariableCache[v.name] = v.value;
              });

              console.log(
                LOG_TAG,
                "Cached variables:",
                engagementVariableCache
              );

              await getEngagementStatus();
              // Redux 
            }
          );
          /* ------------------------------------------------------------------ */
          /* Engagement lifecycle listeners                                      */
          /* ------------------------------------------------------------------ */

          zoomSdk.addEventListener("onEngagementStatusChange", getEngagementStatus);
          zoomSdk.addEventListener("onEngagementContextChange", getEngagementStatus);

          await getEngagementStatus();
          await getUser();
          zoomSdk.addEventListener(
            "onEngagementStatusChange",
            (message: any) => {
              console.log(LOG_TAG, "onEngagementStatusChange event: ");
              console.log(LOG_TAG, message);
              /* {
                        "engagementContext": {
                            "acceptTime": 1716568500851,
                            "engagementId": "DbiEojg6SOaLkuWocMefag",
                            "isTransfer": "false",
                            "queueId": "1Cwu2oh1Slm_NVYngjSkRQ",
                            "queueName": "Demo",
                            "startTime": 1716568498666
                        },
                        "timestamp": 1716568500
                    } */
              // let preEngagementId = sessionStorage.getItem("engagementId");

              getEngagementStatus();
            }
          );

          console.log(LOG_TAG, "Engagement handlers installed!");

          // Register Zoom Phone event handlers
          registerPhoneContextEvent();
          // Bootstrap Contact Center engagement info when supported
          try {
            const runningContext = await zoomSdk.getRunningContext();
            if (runningContext.context === "inContactCenter") {
              const result = await zoomSdk.getEngagementContext();
              const engagementContext = result.engagementContext;
              console.log(LOG_TAG, "engagement context: ", engagementContext);
              await getEngagementStatus();
            } else if (runningContext.context === "inPhone") {
              await getPhoneContext();
              console.log(LOG_TAG, "running context: ", runningContext);
            }
 
          } catch (e) {
            console.log(
              LOG_TAG,
              "Engagement context not available in this environment.",
              e
            );
          }

          // Zoom Phone event handling

        } catch (error) {
          throw error;
        }
      };

      const getEngagementStatus = async () => {
        
        const runningContext = await zoomSdk.getRunningContext();
        if (runningContext.context === "inPhone") {
          //await getPhoneContext();
          return;
        }
        
        let result = await zoomSdk.getEngagementStatus();
        const status = result?.engagementStatus;
        console.log(LOG_TAG, "engagement status: ", result.engagementStatus);
        sessionStorage.setItem(
          "ani",
          result.engagementStatus.consumers[0].consumerNumber
        );
        sessionStorage.setItem("dnis", "+17018854475");
        sessionStorage.setItem(
          "engagementId",
          result.engagementStatus.engagementId
        );
        console.log(LOG_TAG, "engagementStatus State: ", result.engagementStatus.state);
        const state = result.engagementStatus.state;
        console.log(LOG_TAG, "engagementStatus State state: ",  state);
          // Read variables safely
          let createdKey = "";
          let epiId = "";
          let dnis = "";
          
       if(status?.engagementId){ //engagementId
      
          console.log(LOG_TAG, "Engagement status:", status);

          // New engagement detected
          if (currentEngagementId !== status.engagementId) {
            currentEngagementId = status.engagementId;
            engagementVariableCache = {};
          }

          // Hydrate cache
          const systemVariables: EngagementVariableResponse =
            await zoomSdk.getEngagementVariableValue({
              engagementId: status.engagementId,
            });

          systemVariables?.variables?.forEach((v) => {
            engagementVariableCache[v.name] = v.value;
          });

          console.log(LOG_TAG, "Hydrated variables:", engagementVariableCache);

          // Read variables safely
         createdKey = getEngagementVariable("default.created_key");
          epiId = getEngagementVariable("default.EPI_ID");
          dnis = getEngagementVariable("Engagement.DNIS");

          // if (dnis.length > 10) {
          //   dnis = dnis.slice(from, to);
          // }

          console.log(LOG_TAG, "created_key:", createdKey);
          console.log(LOG_TAG, "EPI_ID:", epiId);
          console.log(LOG_TAG, "DNIS:", dnis);
       }
        // const createdKey = getEngagementVariable("default.created_key");
        // const epiId = getEngagementVariable("default.EPI_ID");
        // let dnis_lst = getEngagementVariable("Engagement.DNIS");
        // if (dnis_lst.length > 10) {
        //   dnis_lst = dnis_lst.slice(from, to);
        // }

        if (state == "end") {
          sessionStorage.removeItem("engagementId");
          sessionStorage.removeItem("ani");
          sessionStorage.removeItem("dnis");
          sessionStorage.clear();
          sessionStorage.removeItem("createdKey");
          engagementVariableCache = {};
          currentEngagementId = null;
          // TODO: reset the state
        console.log(LOG_TAG, "engagementStatus State inside if : ", result.engagementStatus.state);

          store.dispatch({
            type: "one-state-subject/reset",
            payload: {},
          });
          store.dispatch({
            type: "one-state-choosecaller/reset",
            payload: {
              visible: false,
              start: false,
            },
          });
          // RESET Page to intial screen on Call END
          store.dispatch({
            type: "one-state-tabs/set",
            payload: "1",
          });
       
          store.dispatch({
            type: "one-state-selectedsubject/reinit",
          });
          store.dispatch({
            type: "one-state-selectedsource/reinit",
          });

          store.dispatch({
            type: "one-state-patient-global-search/set",
            payload: false,
          });
          
        } else {

            // Check if choose caller is already set to avoid multiple dispatches
          sessionStorage.setItem("dnis_lst", dnis);
          sessionStorage.setItem("engagementId", status?.engagementId?status?.engagementId:"");
          sessionStorage.setItem("createdKey", createdKey);
            const chooseCallerState = store.getState().choosecaller;
            if (!chooseCallerState.number && !chooseCallerState.visible) {
                  store.dispatch({
            type: "one-state-choosecaller/set",
            payload: {
              number: result.engagementStatus.consumers[0].consumerNumber,
              dnis: sessionStorage.getItem("dnis"),
              visible: true,
              start: true,
            },
             });
            }
            store.dispatch({
                type: "one-state-getsession/fetch",
                payload: {
                    sessionKey: createdKey // TODO: later we will add proper id
                }
            });
        }
      };

      const getPhoneContext = async (incomingStatus: string = "") => {
        const phoneContext = await zoomSdk.getPhoneContext();
        const {callObject , callStatus ,callId } = phoneContext;  
        console.log(LOG_TAG, "getPhoneContext ", phoneContext);

        try{ 
         
          if(callStatus && callStatus.toLowerCase().includes(PHONE_CALL_STATUS_MAP.INCOMING,PHONE_CALL_STATUS_MAP.OUTGOING)) {
          // no-op
          return;
          }

        if ( incomingStatus.length < 1 && callStatus && callStatus.toLowerCase() === PHONE_CALL_STATUS_MAP.ACTIVE ) {
         
          const {phoneNumber: phoneNumberANI} = callObject?.caller; // ANI
          
          // TODO: Later set dynamic DNIS currently we have done  hard coded DNIS for demo purpose
          const {phoneNumber: phoneNumberDNIS} = callObject?.callee; // DNIS
          // TODO: check if the phone number is extension
          sessionStorage.setItem(
            "ani",
            phoneNumberANI
          );
          sessionStorage.setItem("dnis", "+17018854475");
          sessionStorage.setItem(
            "engagementId",
            callId
          );

          const chooseCallerState = store.getState().choosecaller;
          if (!chooseCallerState.number && !chooseCallerState.visible) {
            store.dispatch({
              type: "one-state-choosecaller/set",
              payload: {
                number: phoneNumberANI,
                dnis: sessionStorage.getItem("dnis"),
                visible: true,
                start: true,
              },
            });
          }
          store.dispatch({
            type: "one-state-getsession/fetch",
            payload: {
              sessionKey: "dummy-id",
            },
          });
        } else  {
          sessionStorage.removeItem("engagementId");
          sessionStorage.removeItem("ani");
          sessionStorage.removeItem("dnis");
          sessionStorage.clear();
          sessionStorage.removeItem("createdKey");
          engagementVariableCache = {};
          currentEngagementId = null;
          // TODO: reset the state
         console.log(LOG_TAG, "engagementStatus State inside if : ", phoneContext);

          store.dispatch({
            type: "one-state-subject/reset",
            payload: {},
          });
          store.dispatch({
            type: "one-state-choosecaller/reset",
            payload: {
              visible: false,
              start: false,
            },
          });
          // RESET Page to intial screen on Call END
          store.dispatch({
            type: "one-state-tabs/set",
            payload: "1",
          });
       
          store.dispatch({
            type: "one-state-selectedsubject/reinit",
          });
          store.dispatch({
            type: "one-state-selectedsource/reinit",
          });

          store.dispatch({
            type: "one-state-patient-global-search/set",
            payload: false,
          });

        }

      } catch (error) {
        console.log(LOG_TAG, "getPhoneContext failed", error);
      }
    }


    const registerPhoneContextEvent = () => {
      zoomSdk.addEventListener("onPhoneContext", async (event: any) => {
        console.log(LOG_TAG, "onPhoneContext event: ", event);
        try {
        await getPhoneContext();
        } catch (err) {
          console.error(LOG_TAG, "getPhoneContext failed", err);
        }
      });

      zoomSdk.addEventListener("onPhoneCalleeAnswered", async (event: any) => {
        console.log(LOG_TAG, "onPhoneCalleeAnswered event: ", event);
        try {
          await getPhoneContext();
        } catch (err) {
          console.error(LOG_TAG, "getPhoneContext failed", err);
        }
      });

      zoomSdk.addEventListener("onPhoneCalleeEnded", async (event: any) => {
        console.log(LOG_TAG, "onPhoneCalleeEnded event: ", event);
        try {
          await getPhoneContext("ENDED"); // dummy status for ended call because no event we are getting when the call is terminated
        } catch (err) {
          console.error(LOG_TAG, "getPhoneContext failed", err);
        }
      });

    zoomSdk.addEventListener("onPhoneCallerEnded", async (event: any) => {
        console.log(LOG_TAG, "onPhoneCallerEnded Caller event: ", event);
        try {
          await getPhoneContext("ENDED"); // dummy status for ended call because no event we are getting when the call is terminated
        } catch (err) {
          console.error(LOG_TAG, "getPhoneContext failed", err);
        }
      });

    }
      const getUser = async () => {
        const currentUrl = window.location.href;

        // Create a URL object
        const url = new URL(currentUrl);

        // Use URLSearchParams to get query parameters
        const params = new URLSearchParams(url.search);

        // Get a specific query parameter
        const zoomUserId = params.get("agent_id");
        const agentName = params.get("agent_name");

        sessionStorage.setItem("agentName", agentName);
        sessionStorage.setItem("zoom_userid", zoomUserId);

        const {
          servers: { locations },numberSlice: { from, to } 
        } = settings;

        let result = await zoomSdk.getAppContext();

        console.log(LOG_TAG, "app context: ", result.context);
        const apiUrl = "https://zoom-patient-assist.patient-assist.agenta-cloud.com/zoom"; // TODO: get from settings
        localStorage.setItem("appContext", result.context);
        fetch(`${apiUrl}/api/zoomapp/get-user`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-zoom-app-context": result.context,
          },
        })
          .then((response) => response.json())
          .then((data) => {
            console.log(LOG_TAG, "zoom user info: ", data);
            /* {
                    "success": true,
                    "user": {
                        "id": "-kKdXPryRW2CYG6MMfEhYQ",
                        "first_name": "SpinSci ",
                        "last_name": "ISV",
                        "display_name": "Rohit Potaraju",
                        "email": "zoom.poc@spinsci.com",
                        "type": 2,
                        "role_name": "Owner",
                        "pmi": 4235354190,
                        "use_pmi": false,
                        "personal_meeting_url": "https://zoom.us/j/4235354190?pwd=bGZkeGFGRUJqSlhUaGtHV2QrQWhMUT09",
                        "timezone": "America/Chicago",
                        "verified": 1,
                        "dept": "",
                        "created_at": "2023-03-16T16:58:40Z",
                        "last_login_time": "2024-05-21T14:13:16Z",
                        "last_client_version": "5.17.8.29318(win)",
                        "cms_user_id": "",
                        "jid": "-kkdxpryrw2cyg6mmfehyq@xmpp.zoom.us",
                        "group_ids": [],
                        "im_group_ids": [],
                        "account_id": "kE2wm2GtRiSwzly2K5EVZg",
                        "language": "en-US",
                        "phone_country": "",
                        "phone_number": "",
                        "status": "active",
                        "job_title": "",
                        "location": "",
                        "login_types": [
                            100
                        ],
                        "role_id": "0",
                        "account_number": 7035782653,
                        "cluster": "aw1",
                        "user_created_at": "2023-03-16T16:58:40Z",
                        "expired_at": 1716478453260
                    }
                } */

            let agentIdValue = sessionStorage.getItem("zoom_userid");
            // Fetching information for getepicuserid
            if (agentIdValue) {
              store.dispatch({
                type: "one-state-getuserid/fetch",
                payload: {
                  agentuserid: agentIdValue,
                },
              });
            }
          })
          .catch((error) => {
            let errorObj: ErrorObject = {
              source: "Zoom Get User",
              type: "error",
              status: error?.status,
              error_date: new Date().toString(),
              error_time: new Date().getTime().toString(),
            };
            store.dispatch({
              type: "one-state-error/add",
              payload: errorObj,
            });
          });
      };

      await configureSdk();
      await getUser();
    })
);
